import React, {useMemo, useRef} from "react";
import {Line} from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip as ChartTooltip,
  Legend as ChartLegend,
  TimeScale,
  ChartOptions,
  ChartData
} from "chart.js";
import "chartjs-adapter-date-fns";
import annotationPlugin from "chartjs-plugin-annotation";

import {chartHeight} from "../../constants";
import colors from "../../styles/colors";
import HelpIcon from "../Help/HelpIcon";
import {useChartTheme} from "../../contexts/ChartThemeContext";
import {ChartErrorBoundary} from "../Charts/ChartErrorBoundary";
import {createChartDataProcessor, validateChartData} from "../../utils/chartDataProcessor";
import {createTickFormatter} from "../../utils/chartTicks";

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  ChartTooltip,
  ChartLegend,
  TimeScale,
  annotationPlugin
);

interface WorldClockOffsetsReportProps {
  points: Array<[number, number]>; // [timestamp, offset]
  title?: string;
  axisSeries?: {
    data: Array<{timestamp: number; value: number}>;
    timeRange?: {start: number; end: number};
    ticks?: Array<{timestamp: number; label: string}>;
  };
}

const WorldClockOffsetsReport: React.FC<WorldClockOffsetsReportProps> = (props) => {
  const chartRef = useRef<ChartJS<"line", {x: number; y: number}[], unknown>>(null);
  const {getThemeColors} = useChartTheme();
  const dataProcessor = createChartDataProcessor();

  // Create tick formatter with consistent formatting
  // Note: Data is pre-localized in parent ChartsReport, so we pass 0 for timezone offset
  const tickFormatter = useMemo(() => {
    return createTickFormatter(0);
  }, []);

  const chartData = useMemo(() => {
    if (!props.points || props.points.length === 0) {
      // Even with no data, create empty data points for proper time axis display
      if (props.axisSeries?.data) {
        return props.axisSeries.data.map((point) => ({
          time: point.timestamp,
          timestamp: point.timestamp,
          offset: 0,
          hasData: false
        }));
      }
      return [];
    }

    const validation = validateChartData(
      props.points.map((point) => ({
        timestamp: point[0],
        value: point[1]
      }))
    );

    if (!validation.isValid) {
      return [];
    }

    // Process data for visualization
    const processedData = props.points.map((point) => ({
      time: point[0],
      timestamp: point[0],
      offset: point[1],
      hasData: true
    }));

    // Data is already optimally sampled by the preset system
    return processedData;
  }, [props.points, dataProcessor]);

  const statistics = useMemo(() => {
    if (chartData.length === 0) {
      return {maxOffset: 0, minOffset: 0, maxRange: 0, hasData: false};
    }

    const offsets = chartData.filter((d) => d.hasData).map((d) => d.offset);
    if (offsets.length === 0) {
      return {maxOffset: 0, minOffset: 0, maxRange: 0, hasData: false};
    }

    const maxOffset = Math.max(...offsets);
    const minOffset = Math.min(...offsets);
    const maxRange = Math.max(Math.abs(maxOffset), Math.abs(minOffset));

    return {
      maxOffset,
      minOffset,
      maxRange,
      hasData: true
    };
  }, [chartData]);

  const themeColors = getThemeColors();

  // Prepare data for Chart.js
  const preparedChartData = useMemo(() => {
    return chartData
      .filter((d) => d.hasData)
      .map((d) => ({
        x: d.timestamp,
        y: d.offset
      }));
  }, [chartData]);

  // Chart.js data configuration
  const data: ChartData<"line", {x: number; y: number}[]> = {
    datasets: [
      {
        label: "Clock Offset",
        data: preparedChartData,
        borderColor: colors.salmonpink,
        backgroundColor: colors.salmonpink + "20",
        borderWidth: 2,
        pointRadius: 0,
        pointHoverRadius: 4,
        stepped: "after", // Step line similar to Recharts "stepAfter"
        tension: 0
      }
    ]
  };

  // Chart.js options configuration
  const options: ChartOptions<"line"> = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      animation: false,
      interaction: {
        mode: "index" as const,
        intersect: false
      },
      onClick: (event, activeElements, chart) => {
        // Double click to reset zoom
        const now = Date.now();
        (chart as any).lastClickTime || 0;
        (chart as any).lastClickTime = now;
      },
      plugins: {
        legend: {
          display: false // We show our own legend above the chart
        },
        tooltip: {
          enabled: true,
          backgroundColor: themeColors.tooltip.background,
          borderColor: themeColors.tooltip.border,
          borderWidth: 1,
          titleColor: themeColors.tooltip.text,
          bodyColor: themeColors.tooltip.text,
          padding: 12,
          displayColors: true,
          callbacks: {
            title: (context) => {
              const timestamp = context[0]?.parsed?.x;
              return timestamp ? new Date(timestamp).toLocaleTimeString() : "";
            },
            label: (context) => {
              const value = context.parsed?.y;
              return value !== null && value !== undefined ? `Offset: ${Math.round(value)}ms` : "";
            }
          }
        },
        annotation: {
          annotations: {
            zeroLine: {
              type: "line" as const,
              scaleID: "y",
              value: 0,
              borderColor: colors.fiestared,
              borderWidth: 2,
              borderDash: [5, 5],
              label: {
                display: true,
                content: "0",
                position: "start" as const,
                backgroundColor: colors.fiestared,
                color: "white",
                font: {
                  size: 11,
                  weight: "bold" as const
                },
                padding: 2
              }
            }
          }
        }
      },
      scales: {
        x: {
          type: "linear",
          display: true,
          title: {
            display: false
          },
          grid: {
            display: true,
            color: themeColors.grid + "30",
            drawOnChartArea: true,
            drawTicks: true
          },
          ticks: {
            color: themeColors.text,
            font: {
              size: 11
            },
            maxTicksLimit: 20,
            autoSkip: true,
            autoSkipPadding: 10,
            callback: function (value) {
              return tickFormatter(value);
            }
          },
          min: props.axisSeries?.timeRange?.start,
          max: props.axisSeries?.timeRange?.end
        },
        y: {
          type: "linear" as const,
          display: true,
          position: "left" as const,
          title: {
            display: true,
            text: "Clock Offset (ms)",
            color: themeColors.text
          },
          grid: {
            display: true,
            color: themeColors.grid + "30",
            drawOnChartArea: true,
            drawTicks: true
          },
          ticks: {
            color: themeColors.text,
            font: {
              size: 11
            }
          },
          suggestedMin: -statistics.maxRange,
          suggestedMax: statistics.maxRange
        }
      }
    }),
    [themeColors, statistics.maxRange, props.axisSeries, tickFormatter, preparedChartData]
  );

  const hasActualData = chartData.some((d) => d.hasData !== false);

  const clockOffsetCount = preparedChartData.length;

  const worldClockLegend = [
    {
      key: "offset",
      label: `${clockOffsetCount} clock offset${clockOffsetCount === 1 ? "" : "s"}`
    }
  ];

  return (
    <ChartErrorBoundary>
      <div>
        <h5>
          World Clock Offset
          <HelpIcon topicId="worldClockOffsets" />
        </h5>

        <div style={{marginBottom: "10px"}}>
          <div style={{display: "flex", alignItems: "center", gap: "10px"}}>
            <div
              style={{
                width: "12px",
                height: "12px",
                backgroundColor: colors.salmonpink,
                borderRadius: "2px"
              }}
            ></div>
            <span style={{color: themeColors.text}}>{worldClockLegend[0].label}</span>
          </div>
        </div>

        <div style={{width: "100%", height: chartHeight, position: "relative"}}>
          <Line ref={chartRef} data={data} options={options} />
        </div>
      </div>
    </ChartErrorBoundary>
  );
};

export default WorldClockOffsetsReport;
